import { Invoices_InvoiceLineProduct } from "data/endpoints";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React, { useEffect, useState } from "react";
import InvoiceLineProductDTO from "../dtos/InvoiceLineProductDTO";
import InvoiceLineProduct from "../components/InvoiceLineProduct";
import PageCard from "features/shared/components/card/PageCard";
import InvoiceLineProductHeader from "../components/InvoiceLineProductHeader";
import InvoiceDTO from "../dtos/InvoiceDTO";
import { InvoiceRecapitulationType } from "../enums/invoiceRecapitulationType";
import InvoiceTotalAmount from "../components/InvoiceTotalAmount";
import InvoiceGridPagination from "../components/InvoiceGridPagination";
import { DataGridId } from "features/shared/components/dataGrid/DataGridId";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import IPaginationHandler from "features/shared/components/pagination/IPaginationHandler";
import { useQuery } from "data/hooks/useQuery";
import { isEmpty } from "features/shared/helpers/objects";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { useTranslation } from "react-i18next";

const pageSize = 5;
const invoiceLinesProduktGridId = DataGridId.invoiceLinesProduktGrid;

interface Props {
  invoice: InvoiceDTO;
}

const InvoiceLinesProductSection = ({ invoice }: Props) => {
  // translations
  const { t: tLabels, i18n } = useTranslation("invoice\\invoice");

  const _paginationHandler = useInjection<IPaginationHandler>(
    IocTypes.PaginationHandler
  );

  // STORE
  const paginationUrlSearchParams = useSelector<IAppState, string>(
    (state) =>
      state.dataGrid.gridSettings[invoiceLinesProduktGridId]
        ?.paginationUrlSearchParams
  );

  const computedQueryParams = useSelector<IAppState, string>(
    (state) =>
      state.dataGrid.gridSettings[invoiceLinesProduktGridId]
        ?.computedUrlSearchParams ?? ""
  );

  // state
  const [invoiceLines, setInvoiceLines] = useState<
    PagedDataResultDTO<InvoiceLineProductDTO>
  >({ result: [], count: 0 });

  // FUNCTIONS
  const handlePaginationOnPageChange = (pageNumber: number) => {
    _paginationHandler.storeSetPagination(
      invoiceLinesProduktGridId,
      new URLSearchParams(paginationUrlSearchParams),
      pageNumber
    );
  };

  const canLoadInvoiceLineProduct = () => {
    return !isEmpty(invoice) && computedQueryParams !== "";
  };

  const { data: invoiceLineProduct, loadStatus: invoiceLineProductStatus } =
    useQuery<PagedDataResultDTO<InvoiceLineProductDTO>>(
      {
        url: Invoices_InvoiceLineProduct,
        routeParams: getRouteParams("invoiceId", invoice.invoiceId!),
        params: new URLSearchParams(computedQueryParams),
      },
      {
        result: [],
        count: 0,
      },
      [invoice, computedQueryParams, i18n.language],
      undefined,
      {
        apply: true,
        condition: canLoadInvoiceLineProduct,
      }
    );

  useEffect(() => {
    setInvoiceLines(invoiceLineProduct);
  }, [invoiceLineProduct.result.length]);

  // Nastavení handleru pro sránkování
  useEffect(() => {
    _paginationHandler.storeSetDefaultParams(
      invoiceLinesProduktGridId,
      new URLSearchParams(paginationUrlSearchParams),
      1,
      pageSize
    );
  }, []);

  // render
  return (
    <PageCard title={tLabels("invoiceLinesProductSection.cardTitle")}>
      <NoContentWrapper
        data={invoiceLineProduct}
        loadStatus={invoiceLineProductStatus}
      >
        <InvoiceLineProductHeader />

        {invoiceLines.result.map((item) => (
          <div key={item.invoiceId} className="py-1 py-sm-2">
            <InvoiceLineProduct invoiceLine={item} />
          </div>
        ))}

        <div className="d-flex flex-column flex-sm-row align-items-center mt-2 mb-1 pt-3 pr-sm-5 border-top">
          <InvoiceGridPagination
            urlParams={new URLSearchParams(paginationUrlSearchParams)}
            count={invoiceLines.count}
            pageSize={pageSize}
            onPageChange={handlePaginationOnPageChange}
          />
          <InvoiceTotalAmount
            invoiceRecapitulation={invoice.invoiceRecapitulation!}
            recapitulationType={InvoiceRecapitulationType.InvoiceLinesTotal}
            currency={invoice.currency!}
            includeTax={false}
          />
        </div>
      </NoContentWrapper>
    </PageCard>
  );
};

export default InvoiceLinesProductSection;
